<script setup>
/**
 * Macros.
 *
 * Archetype E. The macro editor is a separate route and keeps its own back-button
 * layout; this page is only the list, so it gets the shell like Tags and Canned
 * responses do.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { useAdmin } from 'dashboard/composables/useAdmin';

import MacrosTableRow from './MacrosTableRow.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import { BaseTable } from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});
const searchQuery = ref('');

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    useAlert(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
    t('MACROS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('MACROS.HEADER')"
    :description="$t('MACROS.DESCRIPTION')"
  >
    <template #actions>
      <router-link :to="{ name: 'macros_new' }">
        <Button :label="$t('MACROS.HEADER_BTN_TXT')" size="sm" />
      </router-link>
    </template>

    <template #tabs>
      <div class="flex items-center justify-between gap-3">
        <SettingsSearchInput
          v-model="searchQuery"
          :placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
        />
        <span v-if="records.length" class="text-sm text-n-slate-11">
          {{ $t('MACROS.COUNT', { n: records.length }) }}
        </span>
      </div>
    </template>

    <BaseTable
      :headers="tableHeaders"
      :items="filteredRecords"
      :loading="uiFlags.isFetching"
      :no-data-message="
        searchQuery ? $t('MACROS.NO_RESULTS') : $t('MACROS.LIST.404')
      "
    >
      <template #row="{ items }">
        <MacrosTableRow
          v-for="macro in items"
          :key="macro.id"
          :macro="macro"
          :can-manage-public-macros="isAdmin"
          @delete="openDeletePopup(macro)"
        />
      </template>
    </BaseTable>

    <ConfirmDialog
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :description="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
      :description-value="deleteMessage"
      :confirm-button-label="$t('MACROS.DELETE.CONFIRM.YES')"
      :cancel-button-label="$t('MACROS.DELETE.CONFIRM.NO')"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
